// frontend-react/src/components/RuleManagement/RuleUploadModal.tsx
import React, { useMemo, useState } from 'react';
import { Modal, Upload, Space, Button, message, Spin, List as AntList, Tag, Select } from 'antd';
import {
  InboxOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  FileWordOutlined,
  StarOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { RuleDraft, RuleRow, RuleStorageItem } from '../../types/rule';
import { parseCSV, readFileAsText } from '../../services/csvParser';
import {
  uploadRuleSetJson,
  parseDocxFile,
  generateRulesFromDocument,
  getCompanyRiskLevels,
} from '../../services/ruleService';
import RuleSetViewPanel from './RuleSetViewPanel';

type AiSubMode =
  | 'auto'
  | 'standard_contract'
  | 'legal_annotation'
  | 'corporate_policy'
  | 'review_target'
  | 'adhesion_contract'
  | 'adhesion_comparison'
  | 'contract_comparison'
  | 'comparison_auto';

const AI_SUB_MODES: { key: AiSubMode; label: string; accept: string; hint: string }[] = [
  { key: 'auto', label: 'AI 自動辨識', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
  { key: 'standard_contract', label: '標準合約', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
  { key: 'legal_annotation', label: '法務註解', accept: '.docx', hint: '.docx（需含 Word 註解）' },
  { key: 'corporate_policy', label: '企業政策', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
  { key: 'review_target', label: '待審閱文件', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
  { key: 'adhesion_contract', label: '定型化契約', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
];

const COMPARISON_SUB_MODES: { key: AiSubMode; label: string; accept: string; hint: string }[] = [
  { key: 'comparison_auto', label: 'AI 自動辨識', accept: '.docx,.pdf', hint: '.docx  /  .pdf' },
  { key: 'adhesion_comparison', label: '定型化契約比對', accept: '.docx,.pdf', hint: '上傳政府標準範本' },
  { key: 'contract_comparison', label: '合約版本比對', accept: '.docx,.pdf', hint: '上傳法務原始版本' },
];

interface RuleUploadModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isAdmin?: boolean;
  showAiMode?: boolean;
  /** 從比對頁面開啟時為 true，自動進入比對模式（無分頁切換） */
  isComparisonMode?: boolean;
}

const RuleUploadModal: React.FC<RuleUploadModalProps> = ({
  visible,
  onClose,
  onSuccess,
  isAdmin = false,
  showAiMode = false,
  isComparisonMode = false,
}) => {
  const [step, setStep] = useState<number>(1);
  // 比對模式預設選 AI 生成規則；全文審閱預設選自訂規則
  const [topMode, setTopMode] = useState<'structured' | 'ai'>(isComparisonMode ? 'ai' : 'structured');
  // 比對模式預設選 AI 自動辨識；審閱模式預設 auto
  const [aiSubMode, setAiSubMode] = useState<AiSubMode>(isComparisonMode ? 'comparison_auto' : 'auto');

  const isComparisonTab = isComparisonMode;
  const effectiveTopMode = topMode;
  const effectiveSubModes = isComparisonTab ? COMPARISON_SUB_MODES : AI_SUB_MODES;
  const [files, setFiles] = useState<File[]>([]);

  const [previewData, setPreviewData] = useState<RuleDraft[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [ruleSetName, setRuleSetName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [sourceFileToken, setSourceFileToken] = useState<string | null>(null);

  // 風險等級（已確認的值）
  const [riskHigh, setRiskHigh] = useState('');
  const [riskMedium, setRiskMedium] = useState('');
  const [riskLow, setRiskLow] = useState('');
  const [defaultRiskLevels, setDefaultRiskLevels] = useState<Record<string, string>>({});

  // Inline 規則編輯（本地，尚未存 DB）
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);

  const isAI = effectiveTopMode === 'ai';
  const currentAiSub = effectiveSubModes.find(m => m.key === aiSubMode) ?? effectiveSubModes[0]!;
  const effectiveAccept = isAI ? currentAiSub?.accept ?? '.docx,.pdf' : '.csv,.docx';
  const effectiveHint = isAI ? currentAiSub?.hint ?? '.docx  /  .pdf' : '.csv  /  .docx';

  const previewRowsForTable: RuleRow[] = useMemo(() => {
    return previewData.map((r, idx) => ({
      key: r.key || `rule-${idx}`,
      序號: String(r.id ?? idx + 1),
      規則名稱: r.name ?? '',
      規則內容: r.original_text ?? '',
    }));
  }, [previewData]);

  const handleTopModeChange = (mode: 'structured' | 'ai') => {
    if (mode === topMode) return;
    setTopMode(mode);
    setFiles([]);
    setParseError(null);
  };

  const handleAiSubModeChange = (mode: AiSubMode) => {
    setAiSubMode(mode);
    setFiles([]);
    setParseError(null);
  };

  const handleFileAdd = (selectedFile: File) => {
    setParseError(null);
    setFiles(prev => {
      if (prev.some(f => f.name === selectedFile.name && f.size === selectedFile.size)) return prev;
      return [...prev, selectedFile];
    });
    return false;
  };

  const handleFileRemove = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcessFiles = async () => {
    if (files.length === 0) return;
    setParseError(null);

    try {
      if (!isAI) {
        const allRules: RuleDraft[] = [];
        for (const file of files) {
          if (file.name.toLowerCase().endsWith('.docx')) {
            const result = await parseDocxFile(file);
            if (result.success && result.rules) {
              allRules.push(...result.rules);
            } else {
              setParseError(`${file.name}: ${result.error || 'DOCX 解析失敗'}`);
              return;
            }
          } else {
            const text = await readFileAsText(file);
            allRules.push(...parseCSV(text));
          }
        }
        const renumbered = allRules.map((r, idx) => ({
          ...r,
          key: `rule-${idx}`,
          id: String(idx + 1),
        }));
        setPreviewData(renumbered);
        setRuleSetName((files[0] ?? { name: '' }).name.replace(/\.(csv|docx)$/i, ''));
        getCompanyRiskLevels().then(defs => {
          setDefaultRiskLevels(defs);
          setRiskHigh(defs.high || '');
          setRiskMedium(defs.medium || '');
          setRiskLow(defs.low || '');
        });
        setStep(2);
      } else {
        setIsProcessing(true);
        const result = await generateRulesFromDocument(files, aiSubMode);
        setIsProcessing(false);

        if (result.success && result.rules) {
          // 比對模式：保存原始檔案 token
          if (result.source_file_token) {
            setSourceFileToken(result.source_file_token);
          }
          // 確保每筆都有 key
          const withKeys = result.rules.map((r: RuleDraft, idx: number) => ({
            ...r,
            key: r.key || `rule-${idx}`,
          }));
          setPreviewData(withKeys);
          setRuleSetName((files[0] ?? { name: '' }).name.replace(/\.(docx|pdf)$/i, ''));
          getCompanyRiskLevels().then(defs => {
            setDefaultRiskLevels(defs);
            setRiskHigh(defs.high || '');
            setRiskMedium(defs.medium || '');
            setRiskLow(defs.low || '');
          });
          setStep(2);
        } else {
          setParseError(result.error || 'AI 分析失敗');
          setPreviewData([]);
        }
      }
    } catch (error) {
      setIsProcessing(false);
      setParseError(error instanceof Error ? error.message : '檔案處理失敗');
      setPreviewData([]);
    }
  };

  const handleConfirmUpload = async () => {
    if (files.length === 0) return;
    if (!ruleSetName.trim()) {
      message.error('請輸入規則集名稱');
      return;
    }
    if (previewData.length === 0) {
      message.error('沒有可上傳的規則資料');
      return;
    }

    setIsUploading(true);
    const rulesToStore: RuleStorageItem[] = previewData.map(r => ({
      id: String(r.id),
      name: r.name,
      original_text: r.original_text,
      ai_text: r.ai_text || '',
      current_version: r.current_version || 'original',
    }));

    const riskConfig: Record<string, string> = {};
    if (riskHigh.trim()) riskConfig.high = riskHigh.trim();
    if (riskMedium.trim()) riskConfig.medium = riskMedium.trim();
    if (riskLow.trim()) riskConfig.low = riskLow.trim();

    const isDefault =
      riskConfig.high === (defaultRiskLevels.high || '').trim() &&
      riskConfig.medium === (defaultRiskLevels.medium || '').trim() &&
      riskConfig.low === (defaultRiskLevels.low || '').trim();

    const result = await uploadRuleSetJson(
      ruleSetName,
      rulesToStore,
      isDefault ? null : Object.keys(riskConfig).length > 0 ? riskConfig : null,
      false,
      isComparisonTab,
      isComparisonTab ? sourceFileToken : null
    );
    if (result.success) {
      const actualName = result.data?.rule_set_name || ruleSetName;
      if (actualName !== ruleSetName.trim()) {
        message.info(`規則集名稱與現有規則集重複，已自動重新命名為「${actualName}」`);
      }
      message.success(
        `規則集「${actualName}」上傳成功，共 ${
          result.data?.rules_count ?? rulesToStore.length
        } 條規則`
      );
      resetAndClose();
      onSuccess();
    } else {
      message.error(`上傳失敗: ${result.error}`);
    }
    setIsUploading(false);
  };

  const resetAndClose = () => {
    setStep(1);
    setTopMode(isComparisonMode ? 'ai' : 'structured');
    setAiSubMode(isComparisonMode ? 'comparison_auto' : 'auto');
    setFiles([]);
    setPreviewData([]);
    setSourceFileToken(null);
    setParseError(null);
    setRuleSetName('');
    setIsUploading(false);
    setIsProcessing(false);
    setExpandedRows([]);
    setRiskHigh('');
    setRiskMedium('');
    setRiskLow('');
    setDefaultRiskLevels({});
    setEditingRowKey(null);
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setPreviewData([]);
    setParseError(null);
    setEditingRowKey(null);
  };

  // ── Inline 規則編輯（本地，尚未存 DB）──
  const handleEditRow = (key: string) => setEditingRowKey(key);
  const handleCancelEditRow = () => setEditingRowKey(null);
  const handleSaveRow = (key: string, name: string, content: string) => {
    setPreviewData(prev =>
      prev.map(r => (r.key === key ? { ...r, name, original_text: content } : r))
    );
    setEditingRowKey(null);
  };

  const handleDeleteRow = (key: string) => {
    setPreviewData(prev =>
      prev.filter(r => r.key !== key).map((r, idx) => ({ ...r, id: String(idx + 1) }))
    );
    setEditingRowKey(null);
  };

  // ── 風險等級（本地預覽，Panel 的 callback）──
  const handleSaveRiskLevels = async (levels: Record<string, string> | null) => {
    const resolved = levels ?? defaultRiskLevels;
    setRiskHigh(resolved.high || '');
    setRiskMedium(resolved.medium || '');
    setRiskLow(resolved.low || '');
    return { success: true };
  };

  const handleDownloadPreviewCsv = () => {
    if (previewData.length === 0) return;
    const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [
      ['項次', '規則名稱', '規則內容'].map(escape).join(','),
      ...previewData.map((r, idx) =>
        [String(r.id ?? idx + 1), r.name, r.original_text].map(escape).join(',')
      ),
    ];
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rules_${ruleSetName.trim() || 'preview'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const modalWidth = step === 2 ? 1100 : 680;

  return (
    <Modal
      title={
        <Space>
          <CloudUploadOutlined />
          <span>{step === 1
            ? (isComparisonTab ? '上傳比對文件' : '新增規則集')
            : isAI ? 'AI 生成規則集' : '預覽規則集'}</span>
        </Space>
      }
      open={visible}
      onCancel={resetAndClose}
      footer={
        step === 2 ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack} disabled={isUploading}>
              返回
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadPreviewCsv}
              disabled={previewData.length === 0}
            >
              CSV
            </Button>
            <Button
              type='primary'
              icon={<CloudUploadOutlined />}
              onClick={handleConfirmUpload}
              loading={isUploading}
              disabled={!ruleSetName.trim()}
            >
              確認上傳
            </Button>
          </div>
        ) : null
      }
      width={modalWidth}
      destroyOnHidden
      centered={false}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 130px)',
          overflow: 'auto',
          padding: '16px 4px',
        },
      }}
    >
      {/* ── 步驟 1：選模式 + 上傳 ── */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* ── 自訂規則 / AI 生成規則 卡片 ── */}
          {(showAiMode || isComparisonTab) && (
            <div style={{ display: 'flex', gap: 12 }}>
              <div
                onClick={() => handleTopModeChange('structured')}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: topMode === 'structured' ? '2px solid #1E88A8' : '2px solid #f0f0f0',
                  background: topMode === 'structured' ? '#e6f4f8' : '#fafafa',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FileWordOutlined style={{ fontSize: 26, color: '#1E88A8' }} />
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: topMode === 'structured' ? '#1E88A8' : '#333',
                      }}
                    >
                      自訂規則
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {isComparisonTab ? 'CSV / DOCX 直接匯入規則' : 'CSV / DOCX 直接匯入'}
                    </div>
                  </div>
                </div>
              </div>

              <div
                onClick={() => handleTopModeChange('ai')}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: topMode === 'ai' ? '2px solid #722ed1' : '2px solid #f0f0f0',
                  background: topMode === 'ai' ? '#f9f0ff' : '#fafafa',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <StarOutlined style={{ fontSize: 26, color: '#722ed1' }} />
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: topMode === 'ai' ? '#722ed1' : '#333',
                      }}
                    >
                      AI 生成規則
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {isComparisonTab ? '上傳文件，AI 自動生成比對規則' : '上傳文件，AI 自動生成'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 比對模式 + AI 生成：比對類型選擇器 */}
          {isComparisonTab && isAI && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: 6,
              }}
            >
              <span style={{ fontSize: 13 }}>📋</span>
              <span style={{ fontSize: 12, color: '#0050b3', fontWeight: 600 }}>比對類型：</span>
              <Select
                size='small'
                value={aiSubMode}
                onChange={handleAiSubModeChange}
                style={{ width: 160 }}
              >
                {COMPARISON_SUB_MODES.map(m => (
                  <Select.Option key={m.key} value={m.key}>
                    {m.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}

          {/* 審閱模式：開發模式 AI 分析方式選擇器 */}
          {showAiMode && isAI && !isComparisonTab && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                background: '#fffbe6',
                border: '1px solid #ffe58f',
                borderRadius: 6,
              }}
            >
              <span style={{ fontSize: 13 }}>🔧</span>
              <span style={{ fontSize: 12, color: '#ad6800', fontWeight: 600 }}>開發模式</span>
              <span style={{ fontSize: 12, color: '#595959', marginLeft: 4 }}>AI 分析方式：</span>
              <Select
                size='small'
                value={aiSubMode}
                onChange={handleAiSubModeChange}
                style={{ width: 120 }}
              >
                {AI_SUB_MODES.map(m => (
                  <Select.Option key={m.key} value={m.key}>
                    {m.key === 'auto' ? <strong>{m.label}</strong> : m.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}

          {!isAI && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f9f9f9',
                padding: '10px 14px',
                borderRadius: 6,
                border: '1px solid #f0f0f0',
              }}
            >
              <div style={{ color: '#666', fontSize: 13 }}>
                <FileTextOutlined style={{ marginRight: 6, color: '#1E88A8' }} />
                尚未準備好檔案？可以先下載範本
              </div>
              <Space size={8}>
                <Button
                  icon={<DownloadOutlined />}
                  href='/rule_template_web.csv'
                  download='規則範本.csv'
                  size='small'
                >
                  CSV 範本
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  href='/rule_template_web.docx'
                  download='規則範本.docx'
                  size='small'
                >
                  DOCX 範本
                </Button>
              </Space>
            </div>
          )}

          {isProcessing ? (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <Spin size='large' />
              <div style={{ marginTop: 14, color: '#555', fontSize: 14 }}>
                AI 正在分析文件並生成規則...
                {files.length > 1 && `（共 ${files.length} 個檔案）`}
              </div>
              <div style={{ marginTop: 6, color: '#999', fontSize: 12 }}>
                請稍候...（分析時間會依文件長度而異，通常在 1-5 分鐘以內）
              </div>
            </div>
          ) : (
            <>
              <Upload.Dragger
                accept={effectiveAccept}
                beforeUpload={handleFileAdd}
                showUploadList={false}
                multiple
                style={{ padding: '4px 10px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    padding: '8px 0',
                  }}
                >
                  <InboxOutlined style={{ fontSize: '28px', color: 'var(--color-warning)' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                      點擊或拖拽檔案到此區域
                    </div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      支援 {effectiveHint}，可同時選擇多個檔案
                    </div>
                  </div>
                </div>
              </Upload.Dragger>

              {files.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
                    已選擇 {files.length} 個檔案：
                  </div>
                  <AntList
                    size='small'
                    bordered
                    dataSource={files}
                    renderItem={(f, idx) => (
                      <AntList.Item
                        actions={[
                          <Button
                            key='del'
                            type='text'
                            size='small'
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleFileRemove(idx)}
                          />,
                        ]}
                      >
                        <Space>
                          <FileTextOutlined style={{ color: '#1E88A8' }} />
                          <span style={{ fontSize: 13 }}>{f.name}</span>
                          <Tag color='default' style={{ fontSize: 11 }}>
                            {(f.size / 1024).toFixed(0)} KB
                          </Tag>
                        </Space>
                      </AntList.Item>
                    )}
                  />
                  <Button
                    type='primary'
                    style={{ marginTop: 10, width: '100%' }}
                    onClick={handleProcessFiles}
                  >
                    {isAI
                      ? `開始 AI 分析（${files.length} 個檔案）`
                      : `預覽規則（${files.length} 個檔案）`}
                  </Button>
                </div>
              )}
            </>
          )}

          {parseError && (
            <div
              style={{
                padding: '10px 14px',
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: 4,
                color: '#cf1322',
                fontSize: 13,
              }}
            >
              {parseError}
            </div>
          )}
        </div>
      )}

      {/* ── 步驟 2：預覽確認 ── */}
      {step === 2 && (
        <div>
          <RuleSetViewPanel
            name={ruleSetName || '（未命名）'}
            nameEditable
            onNameChange={setRuleSetName}
            rules={previewRowsForTable}
            expandedRows={expandedRows}
            onExpandChange={setExpandedRows}
            editMode={true}
            editingRowKey={editingRowKey}
            savingRow={false}
            onEditRow={handleEditRow}
            onCancelEdit={handleCancelEditRow}
            onSaveRow={handleSaveRow}
            onDeleteRow={handleDeleteRow}
            showRisk={isAdmin}
            riskLevels={{ high: riskHigh, medium: riskMedium, low: riskLow }}
            defaultRiskLevels={defaultRiskLevels}
            canEditRisk={isAdmin}
            onSaveRiskLevels={handleSaveRiskLevels}
          />
          {parseError && (
            <div
              style={{
                padding: '10px 16px',
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: 4,
                color: '#cf1322',
                margin: '4px 12px 0',
              }}
            >
              {parseError}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default RuleUploadModal;
